import React from 'react'
import { es } from 'date-fns/locale'
import dayjs from 'dayjs'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import './FilterDocumentsDocuments.css'
import { useSearchSalesDocumentsContext } from 'state/context/SearchSalesDocumentsContext'
import {
  regexNT,
  regexPanamaId,
  regexPassport,
  regexRUC,
  statusFilter,
} from 'Utils/constants'
import { t } from 'i18next'

const STATUS_SELECT_SX = {
  height: '48px',
  borderRadius: '6px',
  fontSize: '14px',
  width: '100%',
  backgroundColor: 'white',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#b8bdc6',
    borderWidth: '1px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#b8bdc6',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#2563eb',
    borderWidth: '1px',
  },
  '& .MuiSelect-select': {
    fontSize: '14px',
    padding: '0 9px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSelect-icon': {
    display: 'flex',
    alignItems: 'center',
    top: '50%',
    right: '9px',
    transform: 'translateY(-50%) !important',
  },
}

const POPUP_ICON = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="6"
    viewBox="0 0 10 6">
    <path
      fill="none"
      stroke="#495057"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M1 1l4 4 4-4"
    />
  </svg>
)

/**
 * Generic filter component for sales document search screens.
 *
 * @param {Array}  fields             - Document-specific inputs rendered at the top.
 *                                      Each item: { key, i18nKey, testId }
 * @param {string} statusFilterKey    - Key of statusFilter to use (e.g. 'salesOrder', 'dispatch')
 * @param {string} filterButtonTestId - data-testid for the Filtrar button
 * @param {string} clearButtonTestId  - data-testid for the Limpiar button
 */
export default function FilterSearchDocuments({
  fields,
  statusFilterKey,
  filterButtonTestId,
  clearButtonTestId,
}) {
  const {
    filters,
    updateFilter,
    applyFilters,
    clearFilters,
    loadingData,
    setErrorFormat,
    errorFormat,
  } = useSearchSalesDocumentsContext()

  function handleFilterButton() {
    if (!errorFormat) applyFilters(filters)
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleFilterButton()
  }

  function handleClearFilters() {
    setErrorFormat(false)
    clearFilters()
  }

  const validatorFormat = id => {
    const patterns = [regexPanamaId, regexPassport, regexNT, regexRUC]
    return patterns.some(p => p.test(id))
  }

  const handleStatusChange = event => {
    const selectedValue = event.target.value
    if (selectedValue === '') {
      updateFilter('headerStatus', null, 'filter')
    } else {
      updateFilter('headerStatus', [selectedValue], 'filter')
    }
  }

  const statusOptions = statusFilter[statusFilterKey] || []

  return (
    <div className="filters-container">
      {fields.map(field => (
        <div key={field.key} className="input-group">
          <input
            data-testid={field.testId}
            placeholder={t(field.i18nKey)}
            value={filters?.[field.key] || ''}
            onChange={e => updateFilter(field.key, e.target.value, 'filter')}
            onKeyDown={handleKeyDown}
          />
        </div>
      ))}

      <div className="input-group">
        <input
          data-testid="input-customername-filter"
          placeholder={t('SEARCH_QUOTATION_T.CLIENT_NAME')}
          value={filters?.customerName || ''}
          onChange={e => updateFilter('customerName', e.target.value, 'filter')}
          onKeyDown={handleKeyDown}
        />
      </div>

      <div className="container-document-id">
        <div className="input-group">
          <input
            data-testid="input-customerdoc-filter"
            placeholder="Documento Cliente"
            value={filters?.customerDocumentNumber || ''}
            maxLength={20}
            onChange={event => {
              const validationRegex =
                event.target.value !== ''
                  ? validatorFormat(event.target.value)
                  : true
              setErrorFormat(!validationRegex)
              updateFilter(
                'customerDocumentNumber',
                event.target.value,
                'filter'
              )
            }}
            onKeyDown={handleKeyDown}
          />
        </div>
        {errorFormat && (
          <p className="error">
            {t('DETAIL_DOCUMENTS_T.INTRODUCE_VALID_IDENTIFICATION')}
          </p>
        )}
      </div>

      <div>
        <LocalizationProvider adapterLocale={es} dateAdapter={AdapterDayjs}>
          <DatePicker
            data-testid="input-datefrom-filter"
            value={
              filters?.initDate
                ? dayjs(filters.initDate, 'YYYY-MM-DD HH:mm:ss')
                : null
            }
            format="DD/MM/YYYY"
            maxDate={dayjs()}
            onChange={newValue => {
              if (newValue) {
                const formattedDate = newValue
                  .startOf('day')
                  .format('YYYY-MM-DD HH:mm:ss')
                updateFilter('initDate', formattedDate, 'filter')
              } else {
                updateFilter('initDate', null)
              }
            }}
            disableOpenPicker={false}
            slotProps={{
              textField: {
                className: 'custom-datepicker',
                inputProps: {
                  placeholder: 'Fecha desde',
                  readOnly: true,
                  onKeyDown: e => e.preventDefault(),
                },
              },
            }}
          />
        </LocalizationProvider>
      </div>

      <div>
        <LocalizationProvider adapterLocale={es} dateAdapter={AdapterDayjs}>
          <DatePicker
            data-testid="input-dateto-filter"
            value={
              filters?.endDate
                ? dayjs(filters.endDate, 'YYYY-MM-DD HH:mm:ss')
                : null
            }
            format="DD/MM/YYYY"
            maxDate={dayjs()}
            onChange={newValue => {
              if (newValue) {
                const formattedDate = newValue
                  .endOf('day')
                  .format('YYYY-MM-DD HH:mm:ss')
                updateFilter('endDate', formattedDate, 'filter')
              } else {
                updateFilter('endDate', null)
              }
            }}
            disableOpenPicker={false}
            slotProps={{
              textField: {
                className: 'custom-datepicker',
                inputProps: {
                  placeholder: 'Fecha hasta',
                  readOnly: true,
                  onKeyDown: e => e.preventDefault(),
                },
              },
            }}
          />
        </LocalizationProvider>
      </div>

      <FormControl sx={{ width: '100%' }}>
        <Select
          data-testid="select-status-filter"
          value={
            filters?.headerStatus && filters.headerStatus.length > 0
              ? filters.headerStatus[0]
              : ''
          }
          onChange={handleStatusChange}
          displayEmpty
          IconComponent={props => <span {...props}>{POPUP_ICON}</span>}
          renderValue={value => {
            if (!value)
              return <span style={{ color: '#9ca3af' }}>Filtro por estado</span>
            return (
              statusOptions.find(s => s.HeaderStatusId === value)
                ?.Description || ''
            )
          }}
          MenuProps={{
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: 'white !important',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                borderRadius: '6px',
                marginTop: '4px',
              },
              '& .MuiMenuItem-root': { fontSize: '14px' },
              '& .MuiList-root.MuiList-padding.MuiMenu-list': {
                width: '100%',
              },
            },
          }}
          sx={STATUS_SELECT_SX}>
          {statusOptions.map(status => (
            <MenuItem
              key={status.HeaderStatusId}
              value={status.HeaderStatusId}
              data-testid={`status-option-${status.Description}`}>
              {status.Description}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className="buttons-container">
        <button
          data-testid={filterButtonTestId}
          disabled={loadingData}
          onClick={handleFilterButton}
          className="btn-filter">
          Filtrar
        </button>
        <button
          data-testid={clearButtonTestId}
          disabled={loadingData}
          onClick={handleClearFilters}
          className="btn-clear">
          Limpiar
        </button>
      </div>
    </div>
  )
}
